import * as echarts from "echarts";
import type { PriceDaily } from "epsm-api-types/models";
import { useEffect } from "react";
import {
	formatValue,
	formatValueCurrency,
	formatValueDate,
} from "@/components/widgets/helpers/FormattingHelpers";
import { DEFAULT_COLOR, SECTORS, TIME_RANGE_MAP } from "../../../config";
import type {
	SectorsChartType,
	SectorsTimeRange,
	SectorsType,
} from "../../../types";
import { adaptTickerData } from "../../helpers/adaptTickerData";

const mapFormatValue = {
	Price: (value: number) => formatValueCurrency(value, "N/A"),
	Ratio: (value: number) => formatValue(value, "num", {}, "N/A"),
};

export function useCreateSingleSectorChart(
	chartRef: React.RefObject<HTMLDivElement | null>,
	tickerData: PriceDaily[],
	timeRange: SectorsTimeRange,
	chartType: SectorsChartType,
) {
	const normalizedTickerData = adaptTickerData(
		tickerData,
		chartType,
		timeRange,
	);
	const ticker = (normalizedTickerData[0]?.ticker as SectorsType) || null;
	const preparedClose = TIME_RANGE_MAP[timeRange].closePrice;

	// biome-ignore lint/correctness/useExhaustiveDependencies: chartType is used but changes to it are already reflected via normalizedTickerData dependency
	useEffect(() => {
		if (!chartRef.current || !normalizedTickerData.length || !ticker) return;

		const chart = echarts.init(chartRef.current);
		chart.clear();

		const data = normalizedTickerData
			.map((item) => item[preparedClose])
			.filter((value): value is number => value !== undefined);

		let min = data[0] || 0;
		let max = data[0] || 0;

		for (let i = 1; i < data.length; i++) {
			if (data[i] < min) min = data[i];
			if (data[i] > max) max = data[i];
		}

		const categories = tickerData.map((item) => {
			const date = new Date(item.date);

			return date.toLocaleDateString(navigator.language, {
				month: "short",
				year: "2-digit",
			});
		});

		const option = {
			grid: {
				left: -40,
				right: 0,
				top: 10,
				bottom: 0,
				containLabel: true,
			},
			tooltip: {
				trigger: "axis",
				formatter: (params: { dataIndex: number; value: number }[]) => {
					const date = new Date(tickerData[params[0].dataIndex].date);
					const formattedDate = formatValueDate(date, "Invalid date");

					return `${formattedDate}: ${mapFormatValue[chartType](params[0].value)}`;
				},
				axisPointer: {
					type: "cross",
					label: {
						show: false,
					},
				},
				backgroundColor: "black",
				textStyle: {
					color: "white",
				},
			},
			xAxis: {
				type: "category",
				data: categories,
				axisLine: {
					show: true,
				},
				axisLabel: {
					show: false,
				},
			},
			yAxis: {
				type: "value",
				show: false,
				splitLine: {
					show: false,
				},
				min,
				max,
			},
			series: [
				{
					data,
					type: "line",
					color: SECTORS[ticker]?.color || DEFAULT_COLOR,
				},
			],
		};

		chart.setOption(option);

		return () => {
			chart.dispose();
		};
	}, [chartRef, normalizedTickerData, ticker, preparedClose]);

	return ticker;
}
